import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import {
  BUSINESS_REGION_METRIC_LABEL,
  businessRegionGroupOf,
  type BusinessRegionMetric,
  type BusinessRegionRow,
} from '@bhops/shared';
import { KOREA_DOKDO, KOREA_MAP_VIEWBOX, KOREA_SIDO_SHAPES } from './korea-sido-shapes';

// 2026-10-27 오너 — "한반도 지도에다가 원형그래프로 어느 지역에 많은지 볼 수 있게, 지역 클릭하면 해당 지역에
// 몇 대 있는지 … 확대, 축소는 당연히 돼야하고. 클릭된 지역의 땅은 살짝 올라오는 느낌으로".
//   · 땅 색은 고른 지표가 많을수록 진하다. 시도마다 원을 얹는다 — 많을수록 원이 크고, 숫자는 원 안에.
//   · 휠 · 단추로 확대 · 축소, 끌어서 이동. 원과 글자는 화면에서 같은 크기를 유지해 확대하면 겹친 원이 벌어진다.
//   · 지역을 누르면 그 땅이 위로 살짝 떠오르고(아래에 그림자), 옆 칸에 그 지역 숫자가 나온다.

const MAP_METRICS: BusinessRegionMetric[] = ['liveUnits', 'liveStores', 'ib', 'contracts'];
const DETAIL_METRICS: BusinessRegionMetric[] = [
  'ib',
  'demo',
  'contracts',
  'contractUnits',
  'liveStores',
  'liveUnits',
];
const UNIT: Record<BusinessRegionMetric, string> = {
  ib: '건',
  demo: '건',
  contracts: '건',
  contractUnits: '대',
  liveStores: '곳',
  liveUnits: '대',
};

const { width: W, height: H } = KOREA_MAP_VIEWBOX;
const MIN_W = W / 8;
const ZOOM_STEP = 1.3;

interface MapSize {
  /** 옆 숫자 칸 폭(px) — 지도 폭은 나머지 전부. */
  panelW: number;
  /** 지도 높이(px). */
  mapH: number;
}
const MAP_SIZE_KEY = 'kuun:business-regions:map-size';
const DEFAULT_SIZE: MapSize = { panelW: 180, mapH: 640 };
const PANEL_MIN = 140;
const PANEL_MAX = 420;
const MAP_H_MIN = 280;
const MAP_H_MAX = 1200;

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(Math.max(n, lo), hi);
}

function readMapSize(): MapSize {
  try {
    const raw = window.localStorage.getItem(MAP_SIZE_KEY);
    if (!raw) return DEFAULT_SIZE;
    const v = JSON.parse(raw) as Partial<MapSize>;
    return {
      panelW: clamp(Number(v.panelW) || DEFAULT_SIZE.panelW, PANEL_MIN, PANEL_MAX),
      mapH: clamp(Number(v.mapH) || DEFAULT_SIZE.mapH, MAP_H_MIN, MAP_H_MAX),
    };
  } catch {
    return DEFAULT_SIZE;
  }
}

interface View {
  x: number;
  y: number;
  w: number;
}

function clampView(v: View): View {
  const w = Math.min(Math.max(v.w, MIN_W), W);
  const h = (w / W) * H;
  // 지도가 화면 밖으로 다 빠져나가지 않게 — 가장자리 너머로는 화면 폭의 절반까지만.
  const x = Math.min(Math.max(v.x, -w / 2), W - w / 2);
  const y = Math.min(Math.max(v.y, -h / 2), H - h / 2);
  return { x, y, w };
}

function pct(n: number, total: number): string {
  if (!total) return '0%';
  const v = (n / total) * 100;
  return `${Number.isInteger(v) ? v : v.toFixed(1)}%`;
}

export function KoreaRegionMap({
  rows,
  selected: selectedProp,
  onSelect,
}: {
  rows: BusinessRegionRow[];
  /** 고른 시도 — 주면 밖(권역별 표)과 함께 쓴다. 안 주면 지도 혼자 기억한다. */
  selected?: string | null;
  onSelect?: (sido: string | null) => void;
}) {
  const [metric, setMetric] = useState<BusinessRegionMetric>('liveUnits');
  const [ownSelected, setOwnSelected] = useState<string | null>(null);
  const selected = selectedProp !== undefined ? selectedProp : ownSelected;
  const setSelected = (next: string | null) => {
    setOwnSelected(next);
    onSelect?.(next);
  };
  const [view, setView] = useState<View>({ x: 0, y: 0, w: W });
  // 2026-10-27 오너 "간격 조절해서 지도 화면 자체 크기를 조절할 수 있게" — 지도와 옆 칸 사이 세로 손잡이로
  // 폭을, 지도 아래 가로 손잡이로 높이를 바꾼다. 고른 크기는 이 브라우저에 기억한다.
  const [size, setSize] = useState<MapSize>(readMapSize);
  const resize = useRef<{ x: number; y: number; size: MapSize; axis: 'x' | 'y' } | null>(null);
  useEffect(() => {
    try {
      window.localStorage.setItem(MAP_SIZE_KEY, JSON.stringify(size));
    } catch {
      // 저장이 막힌 브라우저 — 크기는 이번 화면에서만 쓴다.
    }
  }, [size]);
  function startResize(axis: 'x' | 'y', e: PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    resize.current = { x: e.clientX, y: e.clientY, size, axis };
  }
  function moveResize(e: PointerEvent<HTMLDivElement>) {
    const r = resize.current;
    if (!r) return;
    setSize(
      r.axis === 'x'
        ? { ...r.size, panelW: clamp(r.size.panelW - (e.clientX - r.x), PANEL_MIN, PANEL_MAX) }
        : { ...r.size, mapH: clamp(r.size.mapH + (e.clientY - r.y), MAP_H_MIN, MAP_H_MAX) },
    );
  }
  function endResize() {
    resize.current = null;
  }
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ px: number; py: number; view: View; moved: boolean } | null>(null);

  const byRegion = useMemo(() => new Map(rows.map((r) => [r.region, r])), [rows]);
  const total = useMemo(() => {
    const t = Object.fromEntries(DETAIL_METRICS.map((m) => [m, 0])) as Record<
      BusinessRegionMetric,
      number
    >;
    for (const r of rows) for (const m of DETAIL_METRICS) t[m] += r.value[m];
    return t;
  }, [rows]);
  const valueOf = (sido: string) => byRegion.get(sido)?.value[metric] ?? 0;
  const max = Math.max(1, ...KOREA_SIDO_SHAPES.map((s) => valueOf(s.sido)));
  // 원 · 글자 · 들림 높이는 화면에서 같은 크기 — 확대할수록 지도 좌표로는 작아진다.
  const k = view.w / W;
  const h = (view.w / W) * H;

  // 휠 확대는 페이지 스크롤을 막아야 해서 passive:false 로 직접 단다.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      // 2026-10-27 오너 "마우스 휠로 확대 축소하는거도 되게" — 지도 위에서는 휠이 확대 · 축소다.
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      zoomAt(
        e.deltaY < 0 ? 1 / ZOOM_STEP : ZOOM_STEP,
        (e.clientX - rect.left) / rect.width,
        (e.clientY - rect.top) / rect.height,
      );
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  });

  /** fx, fy(0~1) 자리를 고정한 채 factor 배로 폭을 바꾼다(작을수록 확대). */
  function zoomAt(factor: number, fx = 0.5, fy = 0.5) {
    setView((v) => {
      const vh = (v.w / W) * H;
      const px = v.x + fx * v.w;
      const py = v.y + fy * vh;
      const w = Math.min(Math.max(v.w * factor, MIN_W), W);
      const nh = (w / W) * H;
      return clampView({ x: px - fx * w, y: py - fy * nh, w });
    });
  }

  function onPointerDown(e: PointerEvent<SVGSVGElement>) {
    drag.current = { px: e.clientX, py: e.clientY, view, moved: false };
  }
  function onPointerMove(e: PointerEvent<SVGSVGElement>) {
    const dr = drag.current;
    const svg = svgRef.current;
    if (!dr || !svg) return;
    const dx = e.clientX - dr.px;
    const dy = e.clientY - dr.py;
    if (!dr.moved && Math.hypot(dx, dy) < 4) return;
    if (!dr.moved) svg.setPointerCapture(e.pointerId);
    dr.moved = true;
    const rect = svg.getBoundingClientRect();
    const s = dr.view.w / rect.width;
    setView(clampView({ x: dr.view.x - dx * s, y: dr.view.y - dy * s, w: dr.view.w }));
  }
  function onPointerUp() {
    // 끌었던 손을 뗀 것은 클릭이 아니다 — 지역 선택은 click 에서 moved 를 보고 거른다.
    setTimeout(() => {
      drag.current = null;
    }, 0);
  }
  function pick(sido: string) {
    if (drag.current?.moved) return;
    setSelected(selected === sido ? null : sido);
  }

  const sel = selected ? byRegion.get(selected) : undefined;
  // 큰 원을 먼저 그려 작은 원이 위에 보이게 한다.
  const bubbles = KOREA_SIDO_SHAPES.map((s) => ({ s, v: valueOf(s.sido) }))
    .filter(({ v }) => v > 0)
    .sort((a, b) => b.v - a.v)
    .map(({ s, v }) => ({
      s,
      v,
      r: (7 + 29 * Math.sqrt(v / max)) * k,
      on: selected === s.sido,
    }));
  const selShape = selected ? KOREA_SIDO_SHAPES.find((s) => s.sido === selected) : undefined;
  const offMap = rows.filter(
    (r) => !KOREA_SIDO_SHAPES.some((s) => s.sido === r.region) && r.value[metric] > 0,
  );
  const lift = 7 * k;

  return (
    <div data-testid="korea-map" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @keyframes kuun-map-rise { from { transform: translate(0, 0); } to { transform: translate(0, -${lift}px); } }
        @keyframes kuun-map-shadow { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <div style={toolbar}>
        <div role="group" aria-label="지도에 보일 숫자" style={{ display: 'flex', gap: 4 }}>
          {MAP_METRICS.map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={metric === m}
              onClick={() => setMetric(m)}
              style={metric === m ? { ...chip, ...chipOn } : chip}
            >
              {BUSINESS_REGION_METRIC_LABEL[m]}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            aria-label="확대"
            onClick={() => zoomAt(1 / ZOOM_STEP)}
            style={iconBtn}
          >
            +
          </button>
          <button type="button" aria-label="축소" onClick={() => zoomAt(ZOOM_STEP)} style={iconBtn}>
            −
          </button>
          <button
            type="button"
            onClick={() => setView({ x: 0, y: 0, w: W })}
            style={{ ...iconBtn, width: 'auto', padding: '0 8px', fontSize: 12 }}
          >
            원래대로
          </button>
        </div>
      </div>

      {/* 높이는 손잡이로 고른 값이 최소 — 옆 표 칸이 더 길면 그만큼 지도가 늘어 두 칸 키가 같다. */}
      <div style={{ display: 'flex', alignItems: 'stretch', flex: '1 0 auto', height: size.mapH }}>
        <svg
          ref={svgRef}
          data-testid="korea-map-svg"
          viewBox={`${view.x} ${view.y} ${view.w} ${h}`}
          style={mapBox}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          role="img"
          aria-label="시도별 지도"
        >
          <defs>
            <filter id="kuun-map-blur" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation={3 * k} />
            </filter>
          </defs>
          {KOREA_SIDO_SHAPES.map((s) => {
            const v = valueOf(s.sido);
            return (
              <path
                key={s.sido}
                data-testid={`korea-map-sido-${s.sido}`}
                d={s.d}
                fillRule="evenodd"
                role="button"
                tabIndex={0}
                aria-label={`${s.sido} ${BUSINESS_REGION_METRIC_LABEL[metric]} ${v}${UNIT[metric]}`}
                aria-pressed={selected === s.sido}
                onClick={() => pick(s.sido)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pick(s.sido);
                  }
                }}
                style={{
                  fill: `color-mix(in srgb, var(--section-accent, var(--brand)) ${10 + Math.round((v / max) * 55)}%, var(--bg-surface))`,
                  stroke: 'var(--bg-surface)',
                  strokeWidth: 1,
                  vectorEffect: 'non-scaling-stroke',
                  cursor: 'pointer',
                  outline: 'none',
                  opacity: selected && selected !== s.sido ? 0.75 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                <title>{`${s.sido}: ${BUSINESS_REGION_METRIC_LABEL[metric]} ${v}${UNIT[metric]}`}</title>
              </path>
            );
          })}

          {/* 독도(경북 울릉군) — 간이 경계에 없어 두 섬을 점으로 그린다. 실제 크기로는 보이지 않으므로
              화면에서 늘 같은 크기를 유지하고, 누르면 경북이 선택된다. */}
          <g
            data-testid="korea-map-dokdo"
            onClick={() => pick('경북')}
            style={{ cursor: 'pointer' }}
          >
            {KOREA_DOKDO.map((p, i) => (
              <circle
                key={p.name}
                cx={p.x + (i === 0 ? 1.4 : -1.4) * k}
                cy={p.y}
                r={(i === 0 ? 1.8 : 2.2) * k}
                style={{
                  fill: `color-mix(in srgb, var(--section-accent, var(--brand)) ${10 + Math.round((valueOf('경북') / max) * 55)}%, var(--bg-surface))`,
                  stroke: 'var(--fg-muted)',
                  strokeWidth: 0.6,
                  vectorEffect: 'non-scaling-stroke',
                }}
              />
            ))}
            <title>독도, 경북 울릉군</title>
          </g>

          {/* 고른 땅 — 제자리에 그림자를 깔고, 같은 모양을 위로 들어 올린다. */}
          {selShape && (
            <g key={`lift-${selShape.sido}`} style={{ pointerEvents: 'none' }}>
              <path
                d={selShape.d}
                fillRule="evenodd"
                fill="rgba(0,0,0,0.35)"
                filter="url(#kuun-map-blur)"
                style={{ animation: 'kuun-map-shadow 0.25s ease-out both' }}
              />
              <path
                d={selShape.d}
                fillRule="evenodd"
                style={{
                  fill: 'color-mix(in srgb, var(--section-accent, var(--brand)) 80%, var(--bg-surface))',
                  stroke: 'var(--bg-surface)',
                  strokeWidth: 1.5,
                  vectorEffect: 'non-scaling-stroke',
                  animation: 'kuun-map-rise 0.25s ease-out both',
                }}
              />
            </g>
          )}

          {/* 2026-10-27 오너 "많을수록 크기가 커지는 그런거" — 조각 원 대신 꽉 찬 원. 넓이가 수에 비례하고
              숫자는 원 안에 적는다. 큰 원을 먼저 그려 작은 원이 위에 보이게 한다. */}
          {bubbles.map(({ s, v, r, on }) => (
            <g
              key={`bubble-${s.sido}`}
              data-testid={`korea-map-bubble-${s.sido}`}
              onClick={() => pick(s.sido)}
              style={{
                cursor: 'pointer',
                transform: `translate(0, ${on ? -lift : 0}px)`,
                transition: 'transform 0.25s ease-out',
              }}
            >
              <circle
                cx={s.cx}
                cy={s.cy}
                r={r}
                style={{
                  fill: BUBBLE_COLOR,
                  fillOpacity: on ? 0.95 : 0.78,
                  stroke: '#ffffff',
                  strokeWidth: (on ? 2.5 : 1.5) * k,
                }}
              />
              <title>{`${s.sido}: ${v}${UNIT[metric]}, 전국의 ${pct(v, total[metric])}`}</title>
            </g>
          ))}
          {/* 숫자 · 이름은 원을 다 그린 뒤에 — 큰 원 위에 작은 원이 얹혀도 큰 원의 숫자가 가려지지 않는다. */}
          {bubbles.map(({ s, v, r, on }) => (
            <g
              key={`label-${s.sido}`}
              data-testid={`korea-map-label-${s.sido}`}
              style={{
                pointerEvents: 'none',
                transform: `translate(0, ${on ? -lift : 0}px)`,
                transition: 'transform 0.25s ease-out',
              }}
            >
              <text
                x={s.cx}
                y={s.cy}
                textAnchor="middle"
                dominantBaseline="central"
                style={{
                  fontSize: Math.max(9, Math.min(r / k, 22) * 0.75) * k,
                  fontWeight: 800,
                  fill: '#ffffff',
                  paintOrder: 'stroke',
                  stroke: BUBBLE_COLOR,
                  strokeWidth: 2 * k,
                }}
              >
                {v.toLocaleString()}
              </text>
              <text
                x={s.cx}
                y={s.cy + r + 9 * k}
                textAnchor="middle"
                style={{
                  fontSize: 10 * k,
                  fontWeight: 700,
                  fill: 'var(--fg-default)',
                  paintOrder: 'stroke',
                  stroke: 'var(--bg-surface)',
                  strokeWidth: 3 * k,
                }}
              >
                {s.sido}
              </text>
            </g>
          ))}
        </svg>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="지도 폭 조절"
          title="끌어서 지도 폭을 바꿉니다"
          data-testid="korea-map-resize-x"
          onPointerDown={(e) => startResize('x', e)}
          onPointerMove={moveResize}
          onPointerUp={endResize}
          onDoubleClick={() => setSize((v) => ({ ...v, panelW: DEFAULT_SIZE.panelW }))}
          style={splitX}
        >
          <span style={gripX} />
        </div>
        <div
          style={{ ...panel, flexBasis: size.panelW, width: size.panelW }}
          data-testid="korea-map-detail"
        >
          {sel ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{sel.region}</div>
              <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 8 }}>
                {businessRegionGroupOf(sel.region) ?? ''}
              </div>
              {DETAIL_METRICS.map((m) => (
                <div key={m} style={detailRow}>
                  <span style={{ color: 'var(--fg-muted)' }}>
                    {BUSINESS_REGION_METRIC_LABEL[m]}
                  </span>
                  <span>
                    <b>
                      {sel.value[m].toLocaleString()}
                      {UNIT[m]}
                    </b>
                    <span style={{ color: 'var(--fg-muted)', fontSize: 11, marginLeft: 4 }}>
                      {pct(sel.value[m], total[m])}
                    </span>
                  </span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 8 }}>
                오른쪽 숫자는 전국에서 차지하는 비율입니다.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>전국</div>
              {DETAIL_METRICS.map((m) => (
                <div key={m} style={detailRow}>
                  <span style={{ color: 'var(--fg-muted)' }}>
                    {BUSINESS_REGION_METRIC_LABEL[m]}
                  </span>
                  <b>
                    {total[m].toLocaleString()}
                    {UNIT[m]}
                  </b>
                </div>
              ))}
              <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 8 }}>
                지도에서 지역을 누르면 그 지역 숫자가 보입니다.
              </div>
            </>
          )}
        </div>
      </div>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="지도 높이 조절"
        title="끌어서 지도 높이를 바꿉니다"
        data-testid="korea-map-resize-y"
        onPointerDown={(e) => startResize('y', e)}
        onPointerMove={moveResize}
        onPointerUp={endResize}
        onDoubleClick={() => setSize((v) => ({ ...v, mapH: DEFAULT_SIZE.mapH }))}
        style={splitY}
      >
        <span style={gripY} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
        지도 위에서 휠을 굴리거나 + − 단추로 확대하고, 끌어서 옮길 수 있습니다.
      </div>
      {offMap.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 6 }}>
          {`${offMap.map((r) => `${r.region} ${r.value[metric]}${UNIT[metric]}`).join(', ')}는 지도에 없습니다.`}
        </div>
      )}
    </div>
  );
}

const BUBBLE_COLOR = 'var(--robot-installing, #ea8a1a)';

const toolbar: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginBottom: 8,
};
const chip: CSSProperties = {
  height: 26,
  padding: '0 10px',
  borderRadius: 4,
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
  cursor: 'pointer',
};
// border 는 shorthand 로 통째 교체한다. borderColor 만 얹으면 꺼질 때 React 가 그 칸을 비우면서
// 테두리 색까지 지워 검은 테두리가 남는다(2026-10-27 오너 "검은 테두리 남는거 안남게").
const chipOn: CSSProperties = {
  background: 'var(--section-accent, var(--brand))',
  border: '1px solid var(--section-accent, var(--brand))',
  color: '#fff',
  fontWeight: 600,
};
const iconBtn: CSSProperties = {
  ...chip,
  width: 28,
  padding: 0,
  fontSize: 15,
  fontWeight: 700,
};
const mapBox: CSSProperties = {
  // flex-basis 0 — svg 의 원래 비율 폭(높이 × 500/456)에 끌려 칸 밖으로 밀리지 않게.
  flex: '1 1 0',
  minWidth: 160,
  height: '100%',
  background: 'color-mix(in srgb, var(--brand) 5%, var(--bg-surface))',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  touchAction: 'none',
  userSelect: 'none',
};
// 손잡이 — 보이는 막대는 작고, 잡는 자리는 넓게(12~14px) 둔다. 두 번 누르면 처음 크기로.
const splitX: CSSProperties = {
  flex: '0 0 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'col-resize',
  touchAction: 'none',
};
const gripX: CSSProperties = {
  width: 4,
  height: 36,
  borderRadius: 2,
  background: 'var(--border-strong)',
};
const splitY: CSSProperties = {
  height: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'row-resize',
  touchAction: 'none',
};
const gripY: CSSProperties = {
  width: 36,
  height: 4,
  borderRadius: 2,
  background: 'var(--border-strong)',
};
const panel: CSSProperties = {
  flexShrink: 0,
  overflowY: 'auto',
  padding: '10px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  fontSize: 13,
};
const detailRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  padding: '4px 0',
  borderBottom: '1px solid var(--border-subtle)',
};
